import axios from 'axios'

// Dedupes /group/{id} calls. Keyed by groupId+agencyId, value is the in-flight
// or resolved Promise. Concurrent callers share the same promise; completed
// responses are reused for the session. Errors are evicted so a retry can
// re-fetch. Cleared on new-search via clearGroupAvailabilityCache.
const cache = new Map()

const makeCacheKey = ({ groupId, agencyId }) => `${groupId}|${agencyId}`

// Map grpCategoryAllocInfos -> categoryOptions used by group filtering:
// { categoryCode, groupCabinsAvailable }. Availability is the sum of the
// allocation's unassigned cabins (unNamedCabinsQty) and unassigned
// guarantees (unNamedCabinGtysQty); only categories with a non-zero total
// are treated as bookable for the group.
//
// grpCategoryAllocInfos can contain multiple entries for the same
// pricedCategoryCode; the entry with the highest requestSequence is the
// latest and supersedes the others, so we keep only that one per category.
const buildCategoryOptions = group => {
  const latestByCategory = new Map()
  for (const info of group?.grpCategoryAllocInfos || []) {
    const existing = latestByCategory.get(info.pricedCategoryCode)
    if (!existing || (info.requestSequence || 0) > (existing.requestSequence || 0)) {
      latestByCategory.set(info.pricedCategoryCode, info)
    }
  }

  return [...latestByCategory.values()].map(info => ({
    categoryCode: info.pricedCategoryCode,
    groupCabinsAvailable:
      (info.unNamedCabinsQty || 0) + (info.unNamedCabinGtysQty || 0)
  }))
}

export const fetchGroupAvailability = ({ groupId, agencyId }) => {
  const key = makeCacheKey({ groupId, agencyId })
  if (cache.has(key)) return cache.get(key)

  const request = axios
    .get(
      `${
        process.env.REACT_APP_FLOW_ENDPOINT_URL
      }group/${groupId}?agencyId=${agencyId}`
    )
    .then(res => {
      const group = res?.data?.['hydra:member']?.[0]
      if (!group) return {}
      return { ...group, categoryOptions: buildCategoryOptions(group) }
    })
    .catch(err => {
      console.error(err)
      cache.delete(key)
      return {}
    })

  cache.set(key, request)
  return request
}

export const clearGroupAvailabilityCache = () => {
  cache.clear()
}
